/**
 * HTTP transport entry point for Product Brain MCP.
 *
 * Serves the MCP protocol over Streamable HTTP for web clients
 * (Claude web app, API consumers) that can't spawn local processes.
 *
 * Implements the full MCP OAuth 2.1 spec (Nov 2025):
 *   1. Protected Resource Metadata  (/.well-known/oauth-protected-resource)
 *   2. Authorization Server Metadata (/.well-known/oauth-authorization-server)
 *   3. Dynamic Client Registration   (POST /register)
 *   4. Authorization Code + PKCE     (GET/POST /authorize)
 *   5. Token Exchange                (POST /oauth/token)
 *
 * Env:
 *   CONVEX_SITE_URL  — Convex deployment URL (defaults to cloud)
 *   PORT / MCP_PORT  — Listen port (default 3000)
 *   CORS_ORIGINS     — Comma-separated allowed origins (default: all)
 *   PB_MODULES       — Comma-separated modules (default: core; opt in with
 *                       "core,gitchain" for the module-gated `chain` /
 *                       `chain-review` / `map` tools — WP-484 S3, DEC-1466;
 *                       default/full budgets are 11/14 per DEC-1473)
 */

import { createHash, randomUUID } from "node:crypto";
import express from "express";
import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
import { isInitializeRequest } from "@modelcontextprotocol/sdk/types.js";
import rateLimit from "express-rate-limit";

import { bootstrapHttp, DEFAULT_CLOUD_URL } from "./client.js";
import { runWithAuth, hashKey, getKeyState } from "./auth.js";
import { createProductBrainServer, SERVER_VERSION } from "./server.js";
import { initAnalytics, shutdownAnalytics, getPostHogClient } from "./analytics.js";
import { initFeatureFlags } from "./featureFlags.js";
import { appLogoMarkup, appLogoStyles } from "./brand/logo-markup.js";
import {
  signRefreshToken,
  verifyRefreshToken,
} from "./lib/refresh-token.js";
import { planKeyAdmission, countKeySessions } from "./sessionCap.js";
import {
  send429,
  buildRateLimitError,
  buildAuthLockoutError,
  buildSessionCapError,
} from "./httpErrors.js";
import {
  AUTH_FAILURE_MAX,
  AUTH_FAILURE_WINDOW_MS,
  MAX_AUTH_FAILURE_ENTRIES,
  checkAuthBlock,
  recordAuthFailure,
  resolveBearerAuth,
} from "./authLockout.js";
import type { AuthFailureRecord } from "./authLockout.js";

// ── Bootstrap ───────────────────────────────────────────────────────────

bootstrapHttp();
initAnalytics();
initFeatureFlags(getPostHogClient());

const PORT = parseInt(process.env.PORT ?? process.env.MCP_PORT ?? "3002", 10);

function baseUrl(req: any): string {
  const proto = req.headers["x-forwarded-proto"] ?? req.protocol ?? "http";
  const host = req.headers.host ?? `localhost:${PORT}`;
  return `${proto}://${host}`;
}

// ── Express App ─────────────────────────────────────────────────────────

const app = express();
// Required when behind a reverse proxy (e.g. Railway): rate limiter uses X-Forwarded-For
// and throws ERR_ERL_UNEXPECTED_X_FORWARDED_FOR if trust proxy is false.
app.set("trust proxy", 1);
app.use(express.json());

// CORS — defaults to https://claude.ai (per REMOTE.md). Override via CORS_ORIGINS env var.
const ALLOWED_ORIGINS = (process.env.CORS_ORIGINS ?? "https://claude.ai")
  .split(",")
  .map((o) => o.trim())
  .filter(Boolean);

app.use((_req: any, res: any, next: any) => {
  const origin = _req.headers.origin;
  if (origin && ALLOWED_ORIGINS.includes(origin)) {
    res.setHeader("Access-Control-Allow-Origin", origin);
  }
  res.setHeader("Access-Control-Allow-Methods", "GET, POST, DELETE, OPTIONS");
  res.setHeader(
    "Access-Control-Allow-Headers",
    "Content-Type, Authorization, Mcp-Session-Id, Last-Event-Id",
  );
  // Retry-After is exposed so browser MCP clients (default origin https://claude.ai)
  // can read the structured-429 retry timing from JS, not just from the JSON body.
  res.setHeader("Access-Control-Expose-Headers", "Mcp-Session-Id, Retry-After");
  if (_req.method === "OPTIONS") {
    res.status(204).end();
    return;
  }
  next();
});

// ── OAuth: Protected Resource Metadata (RFC 9728) ────────────────────────
// Step 1 of MCP auth: Claude fetches this to discover the authorization server.

app.get("/.well-known/oauth-protected-resource", (req: any, res: any) => {
  const base = baseUrl(req);
  res.json({
    resource: base,
    authorization_servers: [base],
    scopes_supported: ["mcp:tools", "mcp:resources"],
    bearer_methods_supported: ["header"],
  });
});

// ── OAuth: Authorization Server Metadata (RFC 8414) ──────────────────────
// Step 2: Claude fetches this to discover authorize, token, and register endpoints.

app.get("/.well-known/oauth-authorization-server", (req: any, res: any) => {
  const base = baseUrl(req);
  res.json({
    issuer: base,
    authorization_endpoint: `${base}/authorize`,
    token_endpoint: `${base}/oauth/token`,
    registration_endpoint: `${base}/register`,
    response_types_supported: ["code"],
    grant_types_supported: ["authorization_code", "refresh_token"],
    code_challenge_methods_supported: ["S256"],
    token_endpoint_auth_methods_supported: ["none"],
    scopes_supported: ["mcp:tools", "mcp:resources"],
  });
});

// ── OAuth: Rate Limiting (Fix 2) ─────────────────────────────────────────
// Separate, stricter limiter for auth endpoints to prevent brute-force and
// enumeration attacks on the OAuth flow.

const authLimiter = rateLimit({
  windowMs: 60_000,
  max: 20,
  standardHeaders: true,
  legacyHeaders: false,
  message: { error: "Too many auth requests. Try again later." },
});

// ── OAuth: Dynamic Client Registration (RFC 7591) ────────────────────────
// Step 3: Claude registers itself as a client before starting the auth flow.

interface RegisteredClient {
  client_id: string;
  redirect_uris: string[];
  client_name?: string;
  registeredAt: number;
}

const registeredClients = new Map<string, RegisteredClient>();
// Fix 4 — Cap client registrations at 500 to prevent unbounded memory growth.
const MAX_REGISTERED_CLIENTS = 500;

app.post(
  "/register",
  authLimiter,
  express.json(),
  (req: any, res: any) => {
    // Fix 4 — Reject registration when cap is reached.
    if (registeredClients.size >= MAX_REGISTERED_CLIENTS) {
      res.status(503).json({
        error: "server_error",
        error_description: "Registration limit reached. Try again later.",
      });
      return;
    }

    const { redirect_uris, client_name } = req.body;

    if (!Array.isArray(redirect_uris) || redirect_uris.length === 0) {
      res.status(400).json({
        error: "invalid_client_metadata",
        error_description: "redirect_uris is required",
      });
      return;
    }

    const clientId = `pb_client_${randomUUID()}`;
    const client: RegisteredClient = {
      client_id: clientId,
      redirect_uris,
      client_name,
      registeredAt: Date.now(),
    };
    registeredClients.set(clientId, client);

    res.status(201).json({
      client_id: clientId,
      client_name: client_name ?? "MCP Client",
      redirect_uris,
      grant_types: ["authorization_code"],
      response_types: ["code"],
      token_endpoint_auth_method: "none",
    });
  },
);

// ── OAuth: Authorization Code + PKCE ─────────────────────────────────────
// Step 4: User enters their pb_sk_* key, server generates a one-time code.

interface PendingAuth {
  apiKey: string;
  codeChallenge: string;
  redirectUri: string;
  expiresAt: number;
}

const pendingCodes = new Map<string, PendingAuth>();

// Refresh tokens are now stateless HMAC-signed (see lib/refresh-token.ts).
// No in-memory store, no cleanup loop, no per-key caps — they survive
// Railway redeploys, which fixes TEN-1661.
const ACCESS_TOKEN_TTL = 3600; // 1 hour
const ACCESS_TOKEN_TTL_MS = ACCESS_TOKEN_TTL * 1000;

// Fix 1 — Opaque access token store.
// Maps pb_at_<uuid> → { apiKey, createdAt } so the raw pb_sk_* key is never
// exposed through the OAuth flow. Capped at 1000 entries with LRU eviction.
interface AccessTokenEntry {
  apiKey: string;
  createdAt: number;
}
const accessTokens = new Map<string, AccessTokenEntry>();
const MAX_ACCESS_TOKENS = 1000;

setInterval(() => {
  const now = Date.now();
  for (const [code, auth] of pendingCodes) {
    if (now > auth.expiresAt) pendingCodes.delete(code);
  }
  for (const [id, client] of registeredClients) {
    if (now - client.registeredAt > 24 * 60 * 60_000) registeredClients.delete(id);
  }
  // Fix 1 — Evict expired opaque access tokens.
  for (const [token, entry] of accessTokens) {
    if (now - entry.createdAt > ACCESS_TOKEN_TTL_MS) accessTokens.delete(token);
  }
  // Fix 5 — Clean up stale auth failure tracking entries.
  for (const [ip, rec] of authFailures) {
    if (rec.blockedUntil < now && rec.firstFailure + AUTH_FAILURE_WINDOW_MS < now) {
      authFailures.delete(ip);
    }
  }
  // Cap authFailures map size.
  if (authFailures.size > MAX_AUTH_FAILURE_ENTRIES) {
    const sorted = [...authFailures.entries()].sort((a, b) => a[1].firstFailure - b[1].firstFailure);
    for (let i = 0; i < sorted.length - MAX_AUTH_FAILURE_ENTRIES; i++) {
      authFailures.delete(sorted[i][0]);
    }
  }
}, 60_000);

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&"'<>]/g, (c) =>
    ({ "&": "&amp;", '"': "&quot;", "'": "&#39;", "<": "&lt;", ">": "&gt;" })[c]!,
  );
}

// ── Authorize Page Templates ─────────────────────────────────────────────
// Parchment-dark OAuth pages — brand tokens from DEC-419 (brand-tokens.json).
// Monochrome-first per DEC-417, warm temperature per DEC-418.
// Provider-agnostic: copy interpolates registered client_name (RFC 7591),
// never hardcodes "Claude" — any MCP client may reach this page.

function authPageShell(title: string, bodyContent: string, headExtra = ""): string {
  return `<!DOCTYPE html>
<html lang="en" data-theme="parchment-dark"><head>
<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(title)} — Product Brain</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,600&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;700&display=swap">
${headExtra}
<style>
:root{
  --bg:#1a1917;--bg-warm:#201f1c;--surface:#262521;
  --fg1:#e4e0d8;--fg2:#c4bfb4;--fg3:#9a9589;--fg4:#6a6560;--fg-bright:#ffffff;
  --border:rgba(255,255,255,0.07);--border-light:rgba(255,255,255,0.04);
  --accent:#c9b99a;
  --btn-bg:#ffffff;--btn-fg:#1a1917;--btn-hover:#e4e0d8;
  --green:#4ade80;--rose:#ef4444;
  --ghost:rgba(38,37,33,0.55);
  --radius-md:7px;--radius-lg:10px;
  --font-display:"Source Serif 4",ui-serif,Georgia,serif;
  --font-body:"IBM Plex Sans",ui-sans-serif,system-ui,sans-serif;
  --font-mono:"IBM Plex Mono",ui-monospace,"SF Mono",Menlo,monospace;
}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html,body{height:100%}
body{
  font-family:var(--font-body);font-size:13px;line-height:1.45;
  color:var(--fg1);background:var(--bg);
  min-height:100vh;display:grid;place-items:center;padding:24px;
  -webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;
  position:relative;overflow:hidden;
}
body::before{
  content:"";position:fixed;inset:0;
  background:radial-gradient(900px 600px at 50% 50%,rgba(228,224,216,0.025),transparent 60%);
  pointer-events:none;z-index:0;
}
.top-mark{position:fixed;top:22px;left:24px;z-index:5;opacity:0.7}
${appLogoStyles}
.stage{
  width:100%;max-width:460px;text-align:center;
  position:relative;z-index:1;
  display:grid;
}
.panel{
  grid-area:1/1;
  transition:opacity 280ms ease-out,transform 380ms cubic-bezier(.2,.6,.2,1),filter 380ms ease-out;
}
.panel[hidden]{
  display:block !important;
  opacity:0;transform:scale(0.96) translateY(-2px);filter:blur(6px);
  pointer-events:none;
}
.panel:not([hidden]){opacity:1;transform:scale(1);filter:none;pointer-events:auto}

/* eyebrows */
.eyebrow{
  font-family:var(--font-mono);font-size:10px;font-weight:700;
  letter-spacing:0.28em;text-transform:uppercase;color:var(--fg4);
  margin-bottom:24px;display:inline-flex;align-items:center;gap:7px;
}
.eyebrow .dot{width:5px;height:5px;border-radius:50%;background:currentColor}
.eyebrow.danger{color:var(--rose)}
.eyebrow.success{color:var(--green)}
.eyebrow.success .dot{box-shadow:0 0 0 3px rgba(74,222,128,0.18)}

/* form input */
.input-wrap{
  display:flex;align-items:center;background:rgba(0,0,0,0.22);
  border:1px solid var(--border);border-radius:var(--radius-md);
  transition:border-color 200ms ease-out,box-shadow 200ms ease-out;
}
.input-wrap:focus-within{
  border-color:rgba(228,224,216,0.45);
  box-shadow:0 0 0 3px rgba(228,224,216,0.10);
}
.input-wrap.has-error{
  border-color:rgba(239,68,68,0.55);
  box-shadow:0 0 0 3px rgba(239,68,68,0.12);
  animation:shake 360ms cubic-bezier(.36,.07,.19,.97);
}
@keyframes shake{
  10%,90%{transform:translateX(-1px)}20%,80%{transform:translateX(2px)}
  30%,50%,70%{transform:translateX(-4px)}40%,60%{transform:translateX(4px)}
}
.input{
  flex:1;min-width:0;background:transparent;border:0;outline:none;
  padding:16px;
  font-family:var(--font-mono);font-size:14px;color:var(--fg1);letter-spacing:0.02em;
}
.input::placeholder{color:var(--fg4)}
.hint{
  margin-top:10px;font-family:var(--font-mono);font-size:10.5px;
  letter-spacing:0.16em;text-transform:uppercase;
  text-align:left;padding-left:4px;height:14px;color:var(--fg4);
  transition:color 160ms ease-out;
}
.hint.is-error{color:var(--rose)}

/* primary button */
.btn-primary{
  width:100%;height:48px;margin-top:14px;
  border:0;border-radius:var(--radius-md);
  background:var(--btn-bg);color:var(--btn-fg);
  font-family:var(--font-body);font-size:14.5px;font-weight:600;letter-spacing:-0.005em;
  cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:10px;
  transition:background 140ms ease-out,transform 80ms ease-out,opacity 200ms ease-out;
  position:relative;overflow:hidden;
}
.btn-primary:hover:not([disabled]){background:var(--btn-hover)}
.btn-primary:active:not([disabled]){transform:translateY(1px)}
.btn-primary[disabled]{opacity:0.45;cursor:default}
.spin{
  width:14px;height:14px;border-radius:50%;
  border:1.5px solid currentColor;border-top-color:transparent;
  animation:spin 700ms linear infinite;opacity:0.85;
}
@keyframes spin{to{transform:rotate(360deg)}}

/* secondary link */
.small-link{
  margin-top:18px;font-family:var(--font-mono);font-size:11px;
  letter-spacing:0.18em;text-transform:uppercase;color:var(--fg4);
}
.small-link a{
  color:var(--fg3);text-decoration:none;border-bottom:1px dotted currentColor;
  padding-bottom:1px;transition:color 140ms;
}
.small-link a:hover{color:var(--fg1)}

/* orb */
.orb-wrap{
  position:relative;width:160px;height:160px;margin:0 auto 36px;
  display:grid;place-items:center;
}
.orb-ring{position:absolute;border-radius:50%}
.orb-ring.r1{inset:0;border:1px solid rgba(228,224,216,0.06);animation:drift1 40s linear infinite}
.orb-ring.r2{inset:18px;border:1px dashed rgba(228,224,216,0.10);animation:drift2 28s linear infinite}
.orb-ring.r3{inset:38px;border:1px solid rgba(74,222,128,0.20);animation:ringPulse 3.4s ease-in-out infinite}
@keyframes drift1{to{transform:rotate(360deg)}}
@keyframes drift2{to{transform:rotate(-360deg)}}
@keyframes ringPulse{0%,100%{opacity:0.6}50%{opacity:1}}

.orb-wrap.is-verifying .orb-ring.r3{
  border-color:rgba(228,224,216,0.20);
  animation:ringPulse 1.1s ease-in-out infinite;
}
.orb-wrap.is-verifying .orb-core{
  box-shadow:0 0 0 6px rgba(228,224,216,0.04),0 0 22px rgba(228,224,216,0.10),inset 0 0 16px rgba(228,224,216,0.06);
  animation:corePulseNeutral 1.6s ease-in-out infinite;
}
.orb-wrap.is-verifying .orb-dot{background:var(--fg3);box-shadow:0 0 10px rgba(228,224,216,0.4)}
@keyframes corePulseNeutral{
  0%,100%{box-shadow:0 0 0 6px rgba(228,224,216,0.04),0 0 22px rgba(228,224,216,0.10),inset 0 0 16px rgba(228,224,216,0.06)}
  50%{box-shadow:0 0 0 9px rgba(228,224,216,0.07),0 0 32px rgba(228,224,216,0.18),inset 0 0 22px rgba(228,224,216,0.10)}
}

.orb-wrap.is-error .orb-ring.r3{border-color:rgba(239,68,68,0.30);animation:none;opacity:1}
.orb-wrap.is-error .orb-ring.r1,.orb-wrap.is-error .orb-ring.r2{animation-play-state:paused}
.orb-wrap.is-error .orb-core{
  box-shadow:0 0 0 6px rgba(239,68,68,0.05),0 0 22px rgba(239,68,68,0.20),inset 0 0 16px rgba(239,68,68,0.08);
  animation:none;
}
.orb-wrap.is-error .orb-dot{background:var(--rose);box-shadow:0 0 10px rgba(239,68,68,0.6)}

.sat-orbit{position:absolute;inset:0;pointer-events:none}
.sat-orbit.o1{animation:drift1 40s linear infinite}
.sat-orbit.o2{animation:drift2 56s linear infinite}
.sat{
  position:absolute;top:50%;left:50%;
  font-family:var(--font-mono);font-size:9px;font-weight:700;letter-spacing:0.14em;
  background:var(--bg);padding:2px 6px;border-radius:3px;
  border:1px solid var(--border-light);
  transform-origin:0 0;opacity:0;
}
.panel:not([hidden])[data-state="connected"] .sat{animation:satIn 600ms ease-out forwards}
@keyframes satIn{from{opacity:0}to{opacity:1}}
.sat span{display:inline-block;animation:counter 40s linear infinite}
.sat-orbit.o2 .sat span{animation:counter2 56s linear infinite}
@keyframes counter{to{transform:rotate(-360deg)}}
@keyframes counter2{to{transform:rotate(360deg)}}

.orb-core{
  position:relative;width:60px;height:60px;border-radius:50%;
  background:radial-gradient(circle at 50% 45%,#1a1a1a 0%,#0c0c0c 60%,#050505 100%);
  border:1px solid rgba(255,255,255,0.06);
  display:grid;place-items:center;
  box-shadow:0 0 0 6px rgba(74,222,128,0.04),0 0 28px rgba(74,222,128,0.20),inset 0 0 18px rgba(74,222,128,0.08);
  animation:corePulse 3.4s ease-in-out infinite;
}
@keyframes corePulse{
  0%,100%{box-shadow:0 0 0 6px rgba(74,222,128,0.04),0 0 24px rgba(74,222,128,0.18),inset 0 0 16px rgba(74,222,128,0.06)}
  50%{box-shadow:0 0 0 9px rgba(74,222,128,0.06),0 0 40px rgba(74,222,128,0.32),inset 0 0 22px rgba(74,222,128,0.14)}
}
.orb-dot{width:10px;height:10px;border-radius:50%;background:#4ade80;box-shadow:0 0 12px rgba(74,222,128,0.7)}

.core-shockwave{
  position:absolute;inset:0;border-radius:50%;
  border:1px solid rgba(74,222,128,0.6);
  opacity:0;pointer-events:none;
}
.panel:not([hidden])[data-state="connected"] .core-shockwave{animation:shock 1100ms ease-out 200ms}
@keyframes shock{
  0%{opacity:0.7;transform:scale(0.4);border-width:2px}
  100%{opacity:0;transform:scale(2.4);border-width:1px}
}

/* titles */
.ok-title{
  font-family:var(--font-display);font-weight:600;
  font-size:38px;line-height:1.05;letter-spacing:-0.025em;
  color:var(--fg-bright);opacity:0;
}
.panel:not([hidden]) .ok-title{animation:rise 600ms ease-out 380ms forwards}
.ok-lead{
  margin:18px auto 0;max-width:22em;font-size:16px;line-height:1.55;color:var(--fg2);
  opacity:0;
}
.panel:not([hidden]) .ok-lead{animation:rise 600ms ease-out 500ms forwards}
.ok-phrase-row{
  margin-top:14px;display:flex;align-items:center;justify-content:center;
  opacity:0;
}
.panel:not([hidden]) .ok-phrase-row{animation:rise 600ms ease-out 620ms forwards}
.cmd.is-copied .cmd-quote-part{display:none}
.success-cta-wrap{
  margin-top:48px;width:100%;opacity:0;
}
.panel:not([hidden]) .success-cta-wrap{animation:rise 600ms ease-out 780ms forwards}
a.btn-primary.success-cta{color:var(--btn-fg);text-decoration:none}

.cmd{
  display:inline-flex;align-items:center;gap:8px;vertical-align:middle;
  font-family:var(--font-mono);font-size:15px;font-weight:500;color:var(--accent);
  background:rgba(201,185,154,0.08);border:1px solid rgba(201,185,154,0.30);
  padding:5px 10px 5px 12px;border-radius:6px;letter-spacing:0.02em;
  cursor:pointer;user-select:none;
  transition:background 140ms ease-out,border-color 140ms ease-out,color 140ms ease-out;
}
.cmd:hover{background:rgba(201,185,154,0.14);border-color:rgba(201,185,154,0.50);color:#dcc9a4}
.cmd.is-copied{color:var(--green);border-color:rgba(74,222,128,0.35);background:rgba(74,222,128,0.08)}
.cmd .cmd-icon{
  width:12px;height:12px;color:currentColor;opacity:0.75;
  display:inline-grid;place-items:center;
  transition:opacity 140ms;
}
.cmd:hover .cmd-icon{opacity:1}
.cmd.is-copied .cmd-icon{color:var(--green);opacity:1}
.cmd .cmd-icon svg{width:12px;height:12px;stroke:currentColor;fill:none;stroke-width:1.5;stroke-linecap:round;stroke-linejoin:round}

/* error specifics */
.err-title{
  font-family:var(--font-display);font-weight:600;
  font-size:32px;line-height:1.1;letter-spacing:-0.02em;
  color:var(--fg1);margin-bottom:12px;
}
.err-msg{
  font-size:14.5px;line-height:1.55;color:var(--fg3);
  margin-bottom:28px;
}
.err-msg code{
  font-family:var(--font-mono);font-size:12px;
  background:rgba(255,255,255,0.06);padding:1px 5px;border-radius:4px;color:var(--fg2);
}
.err-actions{display:flex;gap:8px}
.btn-secondary{
  flex:1;height:44px;border-radius:var(--radius-md);
  background:transparent;color:var(--fg2);
  border:1px solid var(--border);
  font-family:var(--font-body);font-size:13.5px;font-weight:500;
  cursor:pointer;text-decoration:none;
  display:inline-flex;align-items:center;justify-content:center;
  transition:background 140ms,color 140ms,border-color 140ms;
}
.btn-secondary:hover{background:rgba(255,255,255,0.04);color:var(--fg1);border-color:rgba(255,255,255,0.14)}

/* verifying */
.verifying-eyebrow{
  font-family:var(--font-mono);font-size:10px;
  letter-spacing:0.28em;text-transform:uppercase;color:var(--fg3);
  font-weight:700;margin-bottom:14px;
}
.verifying-title{
  font-family:var(--font-display);font-weight:600;
  font-size:28px;line-height:1.1;color:var(--fg1);margin-bottom:8px;
  letter-spacing:-0.02em;
}
.verifying-sub{font-size:13px;color:var(--fg3);min-height:1.45em}

/* form heading */
.form-title{
  font-family:var(--font-display);font-weight:600;
  font-size:32px;line-height:1.1;letter-spacing:-0.02em;
  color:var(--fg1);margin-bottom:10px;
}
.form-sub{font-size:13.5px;color:var(--fg3);margin-bottom:28px;line-height:1.55}

@keyframes rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}

@media (prefers-reduced-motion: reduce){
  *,*::before,*::after{
    animation-duration:0.01ms !important;animation-iteration-count:1 !important;
    transition-duration:0.01ms !important;
  }
}
</style>
</head><body>
<div class="top-mark">${appLogoMarkup({ size: "sm" })}</div>
<div class="stage">${bodyContent}</div>
</body></html>`;
}

// Provider-agnostic display name. Trims and clamps to keep page chrome stable.
function providerDisplayName(clientName: string | undefined): string {
  const name = (clientName ?? "").trim();
  if (!name) return "your assistant";
  return name.length > 40 ? name.slice(0, 40) + "…" : name;
}

// Inner HTML for the success panel. Used both by the JSON path (cloned client-side
// from a <template>) and by the no-JS server-rendered fallback page.
function successPanelInner(workspaceName: string, redirectUrl: string, providerName: string): string {
  return `
<div class="orb-wrap">
  <div class="orb-ring r1"></div>
  <div class="orb-ring r2"></div>
  <div class="orb-ring r3"></div>
  <div class="sat-orbit o1">
    <span class="sat" style="transform:rotate(20deg) translate(78px) rotate(-20deg);color:#4ade80;animation-delay:600ms"><span>DEC</span></span>
    <span class="sat" style="transform:rotate(140deg) translate(78px) rotate(-140deg);color:#c9b99a;animation-delay:720ms"><span>WP</span></span>
    <span class="sat" style="transform:rotate(260deg) translate(78px) rotate(-260deg);color:#f59e0b;animation-delay:840ms"><span>TEN</span></span>
  </div>
  <div class="sat-orbit o2">
    <span class="sat" style="transform:rotate(80deg) translate(54px) rotate(-80deg);color:#60a5fa;animation-delay:960ms"><span>STD</span></span>
    <span class="sat" style="transform:rotate(220deg) translate(54px) rotate(-220deg);color:#a78bfa;animation-delay:1080ms"><span>INS</span></span>
  </div>
  <div class="core-shockwave"></div>
  <div class="orb-core"><div class="orb-dot"></div></div>
</div>
<div class="eyebrow success"><span class="dot"></span>Connected</div>
<h1 class="ok-title">Product Brain is Live</h1>
<p class="ok-lead">Return to your assistant, then say</p>
<div class="ok-phrase-row">
  <button class="cmd" type="button" data-cmd-pill data-redirect="${esc(redirectUrl)}" aria-label="Copy &quot;Start PB&quot; and return to ${esc(providerName)}">
    <span class="cmd-quote-part" aria-hidden="true">&ldquo;</span><span data-cmd-text>Start PB</span><span class="cmd-quote-part" aria-hidden="true">&rdquo;</span>
    <span class="cmd-icon" aria-hidden="true">
      <svg data-cmd-svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V6a2 2 0 0 1 2-2h9"/></svg>
    </span>
  </button>
</div>
<div class="success-cta-wrap">
  <a class="btn-primary success-cta" href="${esc(redirectUrl)}">Continue in ${esc(providerName)}</a>
</div>
<!-- workspace name retained as data hook for tests, hidden from view -->
<span hidden data-field="ws-name">${esc(workspaceName)}</span>`;
}

function errorPanelInner(title: string, trustedDetailHtml: string, retryUrl: string): string {
  return `
<div class="orb-wrap is-error">
  <div class="orb-ring r1"></div>
  <div class="orb-ring r2"></div>
  <div class="orb-ring r3"></div>
  <div class="orb-core"><div class="orb-dot"></div></div>
</div>
<div class="eyebrow danger"><span class="dot"></span>Couldn't connect</div>
<h2 class="err-title" data-field="err-title">${esc(title)}</h2>
<p class="err-msg" data-field="err-msg">${trustedDetailHtml}</p>
<div class="err-actions">
  <a href="${esc(retryUrl)}" class="btn-secondary" data-retry-link>&larr; Try again</a>
  <a href="https://productbrain.io" target="_blank" rel="noopener noreferrer" class="btn-secondary">Get an API key &rarr;</a>
</div>`;
}

const cmdScript = `
(function(){
  function bindCmd(pill){
    if(!pill||pill.__bound)return;pill.__bound=true;
    var textEl=pill.querySelector('[data-cmd-text]');
    var svgEl=pill.querySelector('[data-cmd-svg]');
    var redirectUrl=pill.getAttribute('data-redirect')||'';
    pill.addEventListener('click',function(){
      var done=function(){
        pill.classList.add('is-copied');
        if(textEl)textEl.textContent='Copied';
        if(svgEl)svgEl.innerHTML='<polyline points="4 12 10 18 20 6"/>';
        setTimeout(function(){if(redirectUrl)window.location.assign(redirectUrl)},900);
      };
      try{
        if(navigator.clipboard&&navigator.clipboard.writeText){
          navigator.clipboard.writeText('Start PB').then(done,done);
        }else{done()}
      }catch(e){done()}
    });
  }
  document.querySelectorAll('[data-cmd-pill]').forEach(bindCmd);
})();
`;

function authorizeFormPage(params: {
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  state: string;
  client_id: string;
  client_name?: string;
}): string {
  const { redirect_uri, code_challenge, code_challenge_method, state, client_id } = params;
  const providerName = providerDisplayName(params.client_name);
  const body = `
<!-- ─── CONNECT ─── -->
<div class="panel" id="p-connect" data-state="connect">
  <div class="eyebrow">Connect Product Brain</div>
  <h1 class="form-title">Paste your API key</h1>
  <p class="form-sub">Give <span data-provider>${esc(providerName)}</span> access to your workspace memory.</p>
  <form method="POST" action="/authorize" id="f" autocomplete="off">
    <input type="hidden" name="redirect_uri" value="${esc(redirect_uri)}">
    <input type="hidden" name="code_challenge" value="${esc(code_challenge)}">
    <input type="hidden" name="code_challenge_method" value="${esc(code_challenge_method)}">
    <input type="hidden" name="state" value="${esc(state)}">
    <input type="hidden" name="client_id" value="${esc(client_id)}">
    <div class="input-wrap" id="iw">
      <input type="password" id="k" name="api_key" class="input input-full" placeholder="pb_sk_…" required autofocus spellcheck="false">
    </div>
    <div class="hint" id="hint" hidden></div>
    <button type="submit" class="btn-primary" id="sb" disabled><span id="bt">Connect</span></button>
  </form>
  <div class="small-link"><a href="https://productbrain.io" target="_blank" rel="noopener noreferrer">No key? Generate one &rarr;</a></div>
</div>

<!-- ─── VERIFYING ─── -->
<div class="panel" id="p-verifying" data-state="verifying" hidden>
  <div class="orb-wrap is-verifying">
    <div class="orb-ring r1"></div>
    <div class="orb-ring r2"></div>
    <div class="orb-ring r3"></div>
    <div class="orb-core"><div class="orb-dot"></div></div>
  </div>
  <div class="verifying-eyebrow">Handshake</div>
  <h2 class="verifying-title">Verifying key…</h2>
  <p class="verifying-sub" id="verify-sub">Checking workspace · …</p>
</div>

<!-- ─── CONNECTED (filled by JS from JSON response) ─── -->
<div class="panel" id="p-connected" data-state="connected" hidden></div>

<!-- ─── ERROR (filled by JS from JSON response) ─── -->
<div class="panel" id="p-error" data-state="error" hidden></div>

<template id="tpl-connected">${successPanelInner("__WS__", "__URL__", "__PROVIDER__")}</template>
<template id="tpl-error">${errorPanelInner("__TITLE__", "__DETAIL__", "__RETRY__")}</template>

<script>
${cmdScript}
(function(){
  var f=document.getElementById('f'),k=document.getElementById('k'),iw=document.getElementById('iw'),hint=document.getElementById('hint'),sb=document.getElementById('sb'),bt=document.getElementById('bt');
  var pConnect=document.getElementById('p-connect'),pVerify=document.getElementById('p-verifying'),pOk=document.getElementById('p-connected'),pErr=document.getElementById('p-error');
  var verifySub=document.getElementById('verify-sub');

  function show(panel){
    [pConnect,pVerify,pOk,pErr].forEach(function(p){
      if(p===panel){p.removeAttribute('hidden')}else{p.setAttribute('hidden','')}
    });
  }

  function syncInput(){
    sb.disabled=!k.value.trim();
    iw.classList.remove('has-error');
    hint.classList.remove('is-error');
    hint.textContent='';
    hint.setAttribute('hidden','');
  }
  k.addEventListener('input',syncInput);
  k.addEventListener('keydown',function(e){
    if(e.key==='Escape'){k.value='';syncInput()}
  });

  function showError(title,detailHtml){
    var tpl=document.getElementById('tpl-error');
    var html=tpl.innerHTML
      .replace('__TITLE__',title.replace(/[<>&]/g,function(c){return{'<':'&lt;','>':'&gt;','&':'&amp;'}[c]}))
      .replace('__DETAIL__',detailHtml)
      .replace('__RETRY__','#');
    pErr.innerHTML=html;
    var retry=pErr.querySelector('[data-retry-link]');
    if(retry){retry.addEventListener('click',function(e){e.preventDefault();show(pConnect);k.focus();k.select()})}
    show(pErr);
  }

  function showSuccess(workspaceName,redirectUrl,providerName){
    var tpl=document.getElementById('tpl-connected');
    var safeWs=String(workspaceName||'').replace(/[<>&]/g,function(c){return{'<':'&lt;','>':'&gt;','&':'&amp;'}[c]});
    var safeProv=String(providerName||'your assistant').replace(/[<>&]/g,function(c){return{'<':'&lt;','>':'&gt;','&':'&amp;'}[c]});
    var safeUrl=String(redirectUrl||'').replace(/"/g,'&quot;').replace(/[<>]/g,function(c){return{'<':'&lt;','>':'&gt;'}[c]});
    var html=tpl.innerHTML.split('__WS__').join(safeWs).split('__URL__').join(safeUrl).split('__PROVIDER__').join(safeProv);
    pOk.innerHTML=html;
    pOk.querySelectorAll('[data-cmd-pill]').forEach(function(pill){
      pill.__bound=false;
    });
    // Re-run binder
    var s=document.createElement('script');s.textContent=${JSON.stringify(cmdScript)};document.body.appendChild(s);s.remove();
    show(pOk);
  }

  f.addEventListener('submit',function(e){
    e.preventDefault();
    var v=k.value.trim();
    if(!v){iw.classList.add('has-error');hint.classList.add('is-error');hint.textContent='Paste your key first';hint.removeAttribute('hidden');return}
    if(v.indexOf('pb_sk_')!==0){iw.classList.add('has-error');hint.classList.add('is-error');hint.textContent='Key must start with pb_sk_';hint.removeAttribute('hidden');return}
    sb.disabled=true;bt.textContent='Verifying';
    show(pVerify);

    var steps=['Checking workspace · …','Loading chain · …','Establishing memory · …'];
    var i=0;verifySub.textContent=steps[0];
    var ti=setInterval(function(){i++;if(i>=steps.length){clearInterval(ti);return}verifySub.textContent=steps[i]},900);

    var minDelay=new Promise(function(r){setTimeout(r,2800)});
    var fd=new FormData(f);
    var body=new URLSearchParams();
    fd.forEach(function(val,key){body.append(key,String(val))});

    var req=fetch('/authorize',{
      method:'POST',
      headers:{'Accept':'application/json','Content-Type':'application/x-www-form-urlencoded'},
      body:body.toString(),
      credentials:'same-origin'
    }).then(function(r){return r.json().then(function(j){return{status:r.status,body:j}})});

    Promise.all([req,minDelay]).then(function(arr){
      clearInterval(ti);
      var res=arr[0];
      if(res.body&&res.body.ok){
        showSuccess(res.body.workspaceName,res.body.redirectUrl,res.body.providerName);
      }else{
        showError(res.body&&res.body.title||'Couldn\\'t connect',res.body&&res.body.detail||'Try again, or generate a new key.');
        sb.disabled=false;bt.textContent='Connect';
      }
    }).catch(function(){
      clearInterval(ti);
      showError('Network error','We couldn\\'t reach Product Brain. Check your connection and try again.');
      sb.disabled=false;bt.textContent='Connect';
    });
  });

  k.focus();
})();
</script>`;
  return authPageShell("Connect Product Brain", body);
}

function authorizeSuccessPage(workspaceName: string, redirectUrl: string, providerName: string): string {
  const body = `
<div class="panel" data-state="connected">
${successPanelInner(workspaceName, redirectUrl, providerName)}
</div>
<script>${cmdScript}</script>`;
  return authPageShell("Connected", body);
}

// security: trustedDetailHtml must be a hardcoded literal — never pass user-controlled data.
function authorizeErrorPage(title: string, trustedDetailHtml: string, retryUrl: string): string {
  const body = `
<div class="panel" data-state="error">
${errorPanelInner(title, trustedDetailHtml, retryUrl)}
</div>`;
  return authPageShell("Connection error", body);
}

app.get("/authorize", authLimiter, (req: any, res: any) => {
  const { redirect_uri, code_challenge, code_challenge_method, state, client_id } = req.query;
  const cid = String(client_id ?? "");
  const clientName = cid && registeredClients.has(cid)
    ? registeredClients.get(cid)!.client_name
    : undefined;
  res.type("html").send(authorizeFormPage({
    redirect_uri: String(redirect_uri ?? ""),
    code_challenge: String(code_challenge ?? ""),
    code_challenge_method: String(code_challenge_method ?? "S256"),
    state: String(state ?? ""),
    client_id: cid,
    client_name: clientName,
  }));
});

app.post(
  "/authorize",
  authLimiter,
  express.urlencoded({ extended: false }),
  async (req: any, res: any) => {
    const { api_key, redirect_uri, code_challenge, code_challenge_method, state, client_id } = req.body;

    // Negotiate response shape: fetch path sets Accept: application/json,
    // form-post no-JS path gets the standard rebranded HTML page.
    const wantsJson = String(req.headers["accept"] ?? "").includes("application/json");

    // Build "retry" URL so error pages can link back to the form.
    const retryParams = new URLSearchParams({
      redirect_uri: redirect_uri ?? "",
      code_challenge: code_challenge ?? "",
      code_challenge_method: code_challenge_method ?? "S256",
      ...(state ? { state } : {}),
      ...(client_id ? { client_id } : {}),
    }).toString();
    const retryUrl = `/authorize?${retryParams}`;

    function sendError(title: string, trustedDetailHtml: string, status = 400): void {
      if (wantsJson) {
        res.status(status).json({ ok: false, title, detail: trustedDetailHtml });
      } else {
        res.status(status).type("html").send(authorizeErrorPage(title, trustedDetailHtml, retryUrl));
      }
    }

    if (!api_key?.startsWith("pb_sk_")) {
      sendError(
        "Invalid key format",
        "API keys start with <code>pb_sk_</code>. Check your key and try again.",
      );
      return;
    }

    // Validate redirect_uri against the registered client's allowed redirects.
    // Open redirect prevention: never trust a request-supplied redirect_uri without
    // checking it was pre-registered during dynamic client registration (RFC 7591).
    //
    // When client_id is provided but unknown (e.g. server restart wiped the in-memory Map
    // after claude.ai cached its client_id from a previous session), auto-re-register using
    // the supplied redirect_uri rather than hard-rejecting. The API key validation below is
    // the primary security gate; basic HTTPS validation guards against degenerate redirect URIs.
    if (!client_id) {
      res.status(400).json({
        error: "invalid_request",
        error_description: "client_id is required",
      });
      return;
    }
    if (!registeredClients.has(client_id)) {
      if (typeof redirect_uri === "string" && redirect_uri.startsWith("https://")) {
        registeredClients.set(client_id, {
          client_id,
          redirect_uris: [redirect_uri],
          registeredAt: Date.now(),
        });
        process.stderr.write(`[authorize] auto-re-registered stale client_id after restart\n`);
      } else {
        res.status(400).json({
          error: "invalid_request",
          error_description: "Unknown client_id and redirect_uri is not a valid https URL",
        });
        return;
      }
    }

    const client = registeredClients.get(client_id)!;
    if (!client.redirect_uris.includes(redirect_uri)) {
      res.status(400).json({
        error: "invalid_request",
        error_description: "redirect_uri does not match any registered redirect for this client",
      });
      return;
    }

    // Validate key against Convex before issuing the code.
    // DEC-789 S2: probe primary then fallback URLs so DEV keys work against PROD Railway MCP.
    let workspaceName = "Your Workspace";
    try {
      const primaryUrl = (process.env.CONVEX_SITE_URL ?? DEFAULT_CLOUD_URL).replace(/\/$/, "");
      const fallbackUrls = (process.env.CONVEX_FALLBACK_URLS ?? "")
        .split(",").map((u) => u.trim().replace(/\/$/, "")).filter(Boolean);
      const candidates = [primaryUrl, ...fallbackUrls];

      let foundUrl: string | undefined;
      let anyDefinitiveReject = false;
      let rateLimitedRetryAfter: number | undefined;

      for (const url of candidates) {
        let checkData: { ok: boolean; error?: string; retryAfterSeconds?: number; workspaceName?: string; deploymentUrl?: string } | null = null;
        try {
          const checkRes = await fetch(`${url}/api/key-check`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${api_key}`, "Content-Type": "application/json" },
            signal: AbortSignal.timeout(5000),
          });
          checkData = await checkRes.json() as { ok: boolean; error?: string; retryAfterSeconds?: number; workspaceName?: string; deploymentUrl?: string };
        } catch {
          // This candidate unreachable — try next.
          continue;
        }
        if (checkData.ok) {
          if (checkData.workspaceName) workspaceName = checkData.workspaceName;
          foundUrl = checkData.deploymentUrl ?? url;
          break;
        }
        // The key IS valid but the workspace is over its request budget — distinct from
        // "key not recognized" (invalid_key/missing_key). Surfacing it as a generic
        // reject previously sent a valid-but-throttled key straight to a 401 "Key not
        // recognized" error, which is both wrong and unactionable (the fix is "wait",
        // not "check your API key").
        if (checkData.error === "rate_limited") {
          rateLimitedRetryAfter = checkData.retryAfterSeconds;
          continue;
        }
        anyDefinitiveReject = true;
      }

      if (!foundUrl) {
        if (rateLimitedRetryAfter != null && !anyDefinitiveReject) {
          sendError(
            "Rate limited",
            `This API key is valid but temporarily rate limited. Try again in ${rateLimitedRetryAfter}s.`,
            429,
          );
          return;
        }
        if (anyDefinitiveReject) {
          sendError(
            "Key not recognized",
            "This API key wasn't found in Product Brain. Check your API Keys in Cortex and try again.",
            401,
          );
          return;
        }
        // All candidates unreachable (network errors only) — fail-open.
        process.stderr.write("[authorize] key-check unavailable — proceeding without validation\n");
      } else {
        getKeyState(api_key).deploymentUrl = foundUrl;
      }
    } catch {
      // Outer guard: fail-open so auth works even if the entire block throws.
      process.stderr.write("[authorize] key-check unavailable — proceeding without validation\n");
    }

    const code = randomUUID();
    pendingCodes.set(code, {
      apiKey: api_key,
      codeChallenge: code_challenge,
      redirectUri: redirect_uri,
      expiresAt: Date.now() + 5 * 60_000,
    });

    const url = new URL(redirect_uri);
    url.searchParams.set("code", code);
    if (state) url.searchParams.set("state", state);
    const redirectUrl = url.toString();
    const providerName = providerDisplayName(client.client_name);

    if (wantsJson) {
      res.json({ ok: true, workspaceName, redirectUrl, providerName });
    } else {
      // No-JS path: server-rendered success page. User clicks the pill or the
      // Primary CTA + copy pill both complete the OAuth redirect — no auto-bounce.
      res.type("html").send(authorizeSuccessPage(workspaceName, redirectUrl, providerName));
    }
  },
);

// ── OAuth: Token Exchange ────────────────────────────────────────────────
// Step 5: Claude exchanges the authorization code (with PKCE verifier) for a token.
// Supports both authorization_code and refresh_token grants.

function issueTokens(apiKey: string): object {
  // Return the pb_sk_* key directly as the access_token so connections
  // survive server restarts. Railway deploys on every git push, wiping
  // in-memory Maps. pb_sk_* keys are long-lived (valid until explicitly
  // revoked in Cortex); actual validity is enforced by Convex per tool call.
  // resolveBearerAuth() already handles pb_sk_* with zero Map lookup.
  //
  // The refresh token is HMAC-signed and self-contained (TEN-1661, lib/
  // refresh-token.ts) so it also survives redeploys. No server-side store.
  return {
    access_token: apiKey,
    token_type: "Bearer",
    // 1-year TTL: actual validity enforced by Convex, not by expiry clock.
    // Long TTL prevents unnecessary refresh cycles after restarts.
    expires_in: 365 * 24 * 3600,
    refresh_token: signRefreshToken(apiKey),
  };
}

app.post(
  "/oauth/token",
  authLimiter,
  express.urlencoded({ extended: false }),
  express.json(),
  (req: any, res: any) => {
    const { grant_type, code, code_verifier, redirect_uri, refresh_token } =
      req.body;

    if (grant_type === "refresh_token") {
      const verified = verifyRefreshToken(refresh_token);
      if (!verified) {
        res.status(400).json({
          error: "invalid_grant",
          error_description: "Invalid or expired refresh token",
        });
        return;
      }
      // Rotation = re-issuance. With stateless tokens there is no Map entry
      // to revoke; the new pair simply takes over from the next request.
      res.json(issueTokens(verified.apiKey));
      return;
    }

    if (grant_type !== "authorization_code") {
      res.status(400).json({ error: "unsupported_grant_type" });
      return;
    }

    const pending = pendingCodes.get(code);
    if (!pending || pending.redirectUri !== redirect_uri) {
      res.status(400).json({ error: "invalid_grant" });
      return;
    }

    // PKCE S256 validation
    const challenge = createHash("sha256")
      .update(code_verifier ?? "")
      .digest("base64url");
    if (challenge !== pending.codeChallenge) {
      pendingCodes.delete(code);
      res.status(400).json({
        error: "invalid_grant",
        error_description: "PKCE verification failed",
      });
      return;
    }

    pendingCodes.delete(code);
    res.json(issueTokens(pending.apiKey));
  },
);

// ── Rate Limiting ────────────────────────────────────────────────────────

const mcpLimiter = rateLimit({
  windowMs: 60_000,
  max: 120,
  standardHeaders: true,
  legacyHeaders: false,
  handler: (req: any, res: any) => {
    const r = req.rateLimit;
    const reset = r?.resetTime?.getTime?.() ?? Date.now() + 60_000;
    send429(res, buildRateLimitError({
      retryAfterSeconds: Math.max(1, Math.ceil((reset - Date.now()) / 1000)),
      limit: r?.limit ?? 120,
      current: r?.used ?? 0,
      id: req.body?.id ?? null,  // mirror the JSON-RPC id when a POST body is present
    }));
  },
});

// ── Auth Failure Backoff (Fix 5) ──────────────────────────────────────────
// Per-IP progressive lockout for failed API key auth attempts to prevent
// brute-force attacks through the MCP endpoints. Policy + Bearer classification
// live in ./authLockout.ts (unit-tested); the state map stays here.
const authFailures = new Map<string, AuthFailureRecord>();

// ── Health Check ─────────────────────────────────────────────────────────

app.get("/health", (_req: any, res: any) => {
  res.json({ status: "ok", version: SERVER_VERSION, transport: "http" });
});

// ── Session Management ──────────────────────────────────────────────────

interface SessionEntry {
  transport: StreamableHTTPServerTransport;
  lastAccess: number;
  // Fix 3 — short hash of the API key that created this session. Used to
  // detect session hijacking when subsequent requests arrive with a different key.
  keyHash: string;
  // Number of POST tool-call requests currently being handled. Incremented at
  // POST start, decremented in finally. A session with inFlight > 0 is never
  // torn by planKeyAdmission; the only exception is the evictStaleSessions TTL
  // backstop, which force-evicts past SESSION_TTL_MS to self-heal a leaked
  // counter (logged as reason=inflight_leak). GET SSE and DELETE do NOT touch
  // this — see design §3.1.
  inFlight: number;
}

const sessions = new Map<string, SessionEntry>();
const SESSION_TTL_MS = 30 * 60 * 1000;
const MAX_SESSIONS = 200;
// Grace window: a session touched within this many ms is "recently active" and
// is never evicted by the per-key planner (covers the gap between back-to-back
// POST calls). See planKeyAdmission in sessionCap.ts.
const EVICT_GRACE_MS = 60_000;
// Fix 6 — MAX_SESSIONS_PER_KEY is an in-process memory/working-set guard, NOT a
// concurrency policy. Raising MCP_MAX_SESSIONS_PER_KEY does NOT grant more
// concurrent agents — it only widens the idle-session reclaim window. It must
// never be the thing that limits how many agents can work; fleet concurrency is
// a kernel concern (TEN-2229), not a per-key surface cap.
// Clamped to [1, MAX_SESSIONS - 1]: a value < 1 would make planKeyAdmission
// throw on every init (RangeError); the MAX_SESSIONS - 1 ceiling guarantees a
// single key can never alone fill the entire global pool (always leaves >= 1
// slot), so a bad env degrades to a safe bound rather than breaking init or
// disabling the cross-key fairness guard. Default 25 is far below this ceiling.
const MAX_SESSIONS_PER_KEY = Math.max(
  1,
  Math.min(MAX_SESSIONS - 1, Number(process.env.MCP_MAX_SESSIONS_PER_KEY) || 25),
);

// The other eviction site is planKeyAdmission (sessionCap.ts), which runs
// per-key on init. Keep both coherent — a change to one may affect the other.
function evictStaleSessions(): void {
  const now = Date.now();
  for (const [id, entry] of sessions) {
    // TTL branch: evict regardless of inFlight once past SESSION_TTL_MS. GET is
    // no longer inFlight-guarded after the round-3 fix, so an unconditional skip
    // on inFlight > 0 could make a session with a *leaked* counter immortal. The
    // hard-age backstop turns that leak into a self-healing, observable event.
    if (now - entry.lastAccess > SESSION_TTL_MS) {
      // Force-evicting an inFlight > 0 session here means the counter leaked
      // (an unexpected throw outside the POST try/finally). Log it as such.
      logSessionLifecycle(
        "session_deleted",
        id,
        entry.inFlight > 0 ? "inflight_leak" : "ttl",
      );
      entry.transport.close().catch(() => {});
      sessions.delete(id);
    }
  }
  if (sessions.size > MAX_SESSIONS) {
    // Global memory-pressure branch: SKIP inFlight > 0 so we can't drop a live
    // POST call mid-flight.
    const sorted = [...sessions.entries()]
      .filter(([, e]) => e.inFlight === 0)
      .sort((a, b) => a[1].lastAccess - b[1].lastAccess);
    const overflow = sessions.size - MAX_SESSIONS;
    for (let i = 0; i < Math.min(overflow, sorted.length); i++) {
      logSessionLifecycle("session_deleted", sorted[i][0], "eviction");
      sorted[i][1].transport.close().catch(() => {});
      sessions.delete(sorted[i][0]);
    }
    // Observability: if every session is inFlight (sorted is empty / too short)
    // the loop above could not bring us under MAX_SESSIONS. We deliberately do
    // NOT tear live POSTs — the TTL backstop reclaims them at SESSION_TTL_MS —
    // but a sustained breach is worth surfacing rather than silently exceeding
    // the global memory ceiling.
    if (sessions.size > MAX_SESSIONS) {
      const ts = new Date().toISOString();
      process.stderr.write(
        `[HTTP] ${ts} session_cap_breach size=${sessions.size} max=${MAX_SESSIONS} ` +
          `reason=all_inflight (no idle sessions to evict; TTL backstop will reclaim)\n`,
      );
    }
  }
}

setInterval(evictStaleSessions, 60_000);

// ── Auth Helpers ─────────────────────────────────────────────────────────
// Bearer classification (absent vs. rejected vs. ok) lives in ./authLockout.ts
// via resolveBearerAuth(); handlers call it directly so that only a
// presented-and-rejected credential accrues toward the lockout (TEN-2606).

function send401(req: any, res: any): void {
  const base = baseUrl(req);
  res
    .status(401)
    .set(
      "WWW-Authenticate",
      `Bearer resource_metadata="${base}/.well-known/oauth-protected-resource"`,
    )
    .json({ error: "unauthorized" });
}

function logRequest(
  method: string,
  outcome: "ok" | "auth_fail" | "error",
  sessionId?: string,
  durationMs?: number,
): void {
  const ts = new Date().toISOString();
  const sid = sessionId ? ` session=${sessionId}` : "";
  const dur = durationMs != null ? ` duration=${durationMs}ms` : "";
  process.stderr.write(`[HTTP] ${ts} ${method} ${outcome}${sid}${dur}\n`);
}

function logSessionLifecycle(
  event: "session_created" | "session_deleted",
  sessionId: string,
  reason?: "ttl" | "eviction" | "onclose" | "lru_evict" | "inflight_leak" | "init_rejected",
): void {
  const ts = new Date().toISOString();
  const r = reason ? ` reason=${reason}` : "";
  process.stderr.write(`[HTTP] ${ts} ${event} session=${sessionId}${r}\n`);
}

// ── MCP Handlers ────────────────────────────────────────────────────────

app.post("/mcp", mcpLimiter, async (req: any, res: any) => {
  // Fix 5 — Block IPs that have exceeded the auth failure threshold.
  const reqIp: string = req.ip ?? "unknown";
  const nowTs = Date.now();
  if (checkAuthBlock(authFailures, reqIp, nowTs)) {
    const rec = authFailures.get(reqIp);
    const retryAfterSeconds = rec ? Math.max(0, Math.ceil((rec.blockedUntil - nowTs) / 1000)) : 0;
    send429(res, buildAuthLockoutError({ retryAfterSeconds, limit: AUTH_FAILURE_MAX, current: rec?.count ?? AUTH_FAILURE_MAX, id: req.body?.id ?? null }));
    return;
  }

  // TEN-2606 — only a *presented-and-rejected* credential accrues toward the
  // lockout; an absent credential is the OAuth bootstrap probe and is never
  // recorded, so a legitimate re-bootstrap can never self-lock its IP.
  const auth = resolveBearerAuth(req.headers?.authorization, accessTokens, nowTs, ACCESS_TOKEN_TTL_MS);
  if (auth.status !== "ok") {
    logRequest("POST", "auth_fail");
    if (auth.status === "rejected") recordAuthFailure(authFailures, reqIp, nowTs);
    send401(req, res);
    return;
  }
  const apiKey = auth.apiKey;

  const sessionId = req.headers["mcp-session-id"] as string | undefined;
  const reqStart = Date.now();

  try {
    await runWithAuth({ apiKey, mcpSessionId: sessionId }, async () => {
      if (sessionId && sessions.has(sessionId)) {
        const entry = sessions.get(sessionId)!;
        // Fix 3 — Verify the session belongs to the presenting key.
        if (entry.keyHash !== hashKey(apiKey)) {
          res.status(403).json({
            jsonrpc: "2.0",
            error: { code: -32000, message: "Session key mismatch" },
            id: null,
          });
          return;
        }
        // inFlight guard (design §3.3): the increment is the FIRST statement
        // inside the try so nothing throwable sits between it and the guarded
        // region; finally decrements and re-stamps lastAccess so a long tool
        // call leaves a fresh stamp (never looks idle mid-flight).
        try {
          entry.inFlight++;
          entry.lastAccess = Date.now();
          await entry.transport.handleRequest(req, res, req.body);
          logRequest("POST", "ok", sessionId, Date.now() - reqStart);
        } finally {
          entry.inFlight--;
          entry.lastAccess = Date.now();
        }
      } else if (!sessionId && isInitializeRequest(req.body)) {
        // Fix 6 — per-key session cap with recency-guarded LRU eviction.
        const keyH = hashKey(apiKey);
        const plan = planKeyAdmission(sessions, keyH, MAX_SESSIONS_PER_KEY, Date.now(),
          { ttlMs: SESSION_TTL_MS, graceMs: EVICT_GRACE_MS });
        for (const sid of plan.evict) {
          const victim = sessions.get(sid);
          sessions.delete(sid);                       // delete before close; onclose guards on membership
          victim?.transport.close().catch(() => {});
          logSessionLifecycle("session_deleted", sid, "lru_evict");
        }
        if (!plan.admit) {
          // poll-vs-deadline is decided by the planner from the same snapshot it
          // evicted against (plan.retryIsPoll) — no second scan / clock read here.
          send429(res, buildSessionCapError({
            retryAfterSeconds: plan.retryAfterSeconds,
            limit: MAX_SESSIONS_PER_KEY,
            current: countKeySessions(sessions, keyH),
            poll: plan.retryIsPoll,
            id: req.body?.id ?? null,
          }));
          return;
        }
        const sid = randomUUID();
        let initialized = false;                      // set true once the SDK confirms the session initialized
        const transport = new StreamableHTTPServerTransport({
          sessionIdGenerator: () => sid,
          onsessioninitialized: (s: string) => { initialized = true; logSessionLifecycle("session_created", s); }, // log only — no map write
        });
        const reserved: SessionEntry = { transport, lastAccess: Date.now(), keyHash: keyH, inFlight: 1 };
        sessions.set(sid, reserved);                  // synchronous single-writer reservation (closes TOCTOU)
        transport.onclose = () => {
          const s = transport.sessionId ?? sid;
          if (sessions.has(s)) { sessions.delete(s); logSessionLifecycle("session_deleted", s, "onclose"); }
        };
        try {
          const server = createProductBrainServer();
          await server.connect(transport);
          await transport.handleRequest(req, res, req.body);
          logRequest("POST", "ok", transport.sessionId ?? undefined, Date.now() - reqStart);
        } catch (e) {
          transport.onclose = undefined as any;        // detach → no phantom delete log on failure
          sessions.delete(sid);
          throw e;
        } finally {
          // handleRequest can reject an init-shaped POST with a 4xx WITHOUT throwing
          // (e.g. missing "Accept: application/json, text/event-stream", bad content
          // type, invalid batch init) — onsessioninitialized never fires and no
          // session id reaches the client, so the reservation would otherwise linger
          // until grace/TTL and let a broken/malicious client pin per-key slots.
          // Drop the orphan reservation immediately when init did not complete.
          if (!initialized) {
            transport.onclose = undefined as any;
            if (sessions.delete(sid)) logSessionLifecycle("session_deleted", sid, "init_rejected");
          } else {
            const e = sessions.get(sid); if (e) { e.inFlight--; e.lastAccess = Date.now(); }
          }
        }
      } else if (sessionId) {
        // Stale Mcp-Session-Id (client cached one from before a Railway restart
        // or after TTL eviction). Per MCP Streamable HTTP spec § Session
        // Management clause 3, the server MUST respond with HTTP 404 Not Found
        // so the client (clause 4) re-initialises with a fresh InitializeRequest
        // without prompting OAuth re-auth. Returning 400 here was the root cause
        // of "Claude.ai re-auths after every deploy".
        process.stderr.write(
          `[HTTP] ${new Date().toISOString()} session_stale sessionId=${sessionId} (likely server restart — instructing client to re-initialise)\n`,
        );
        res.status(404).json({
          jsonrpc: "2.0",
          error: { code: -32001, message: "Session not found — re-initialise" },
          id: null,
        });
      } else {
        // Non-initialise request with no Mcp-Session-Id — per spec clause 2,
        // servers that require a session ID SHOULD respond with HTTP 400.
        process.stderr.write(
          `[HTTP] ${new Date().toISOString()} session_invalid no valid session ID (client may have omitted Mcp-Session-Id)\n`,
        );
        res.status(400).json({
          jsonrpc: "2.0",
          error: { code: -32000, message: "Bad Request: no valid session ID provided" },
          id: null,
        });
      }
    });
  } catch (err: any) {
    logRequest("POST", "error", sessionId, Date.now() - reqStart);
    if (!res.headersSent) {
      res.status(500).json({
        jsonrpc: "2.0",
        error: { code: -32603, message: "Internal server error" },
        id: null,
      });
    }
  }
});

app.get("/mcp", mcpLimiter, async (req: any, res: any) => {
  // Fix 5 — Block IPs that have exceeded the auth failure threshold.
  const reqIp: string = req.ip ?? "unknown";
  const nowTs = Date.now();
  if (checkAuthBlock(authFailures, reqIp, nowTs)) {
    const rec = authFailures.get(reqIp);
    const retryAfterSeconds = rec ? Math.max(0, Math.ceil((rec.blockedUntil - nowTs) / 1000)) : 0;
    send429(res, buildAuthLockoutError({ retryAfterSeconds, limit: AUTH_FAILURE_MAX, current: rec?.count ?? AUTH_FAILURE_MAX, id: req.body?.id ?? null }));
    return;
  }

  // TEN-2606 — absent credential (OAuth bootstrap probe) is never recorded.
  const auth = resolveBearerAuth(req.headers?.authorization, accessTokens, nowTs, ACCESS_TOKEN_TTL_MS);
  if (auth.status !== "ok") {
    logRequest("GET", "auth_fail");
    if (auth.status === "rejected") recordAuthFailure(authFailures, reqIp, nowTs);
    send401(req, res);
    return;
  }
  const apiKey = auth.apiKey;

  const sessionId = req.headers["mcp-session-id"] as string | undefined;
  if (!sessionId) {
    res.status(400).send("Missing Mcp-Session-Id header");
    return;
  }
  if (!sessions.has(sessionId)) {
    // Stale session — per MCP spec § Session Management clauses 3-4, return 404
    // so the client re-initialises without re-authing. Survives Railway restarts.
    process.stderr.write(
      `[HTTP] ${new Date().toISOString()} session_stale GET sessionId=${sessionId}\n`,
    );
    res.status(404).send("Session not found — re-initialise");
    return;
  }

  try {
    await runWithAuth({ apiKey, mcpSessionId: sessionId }, async () => {
      const entry = sessions.get(sessionId)!;
      // Fix 3 — Verify the session belongs to the presenting key.
      if (entry.keyHash !== hashKey(apiKey)) {
        res.status(403).json({
          jsonrpc: "2.0",
          error: { code: -32000, message: "Session key mismatch" },
          id: null,
        });
        return;
      }
      // NOT inFlight-guarded (design §3.1, round-3 review): the SSE
      // handleRequest stays open for the stream's whole life, so guarding it
      // would pin every active session → permanent 429 lockout. Re-stamp
      // lastAccess on stream open; the recency guard alone governs evictability.
      entry.lastAccess = Date.now();
      await entry.transport.handleRequest(req, res);
      logRequest("GET", "ok", sessionId);
    });
  } catch {
    logRequest("GET", "error", sessionId);
  }
});

app.delete("/mcp", mcpLimiter, async (req: any, res: any) => {
  // Fix 5 — Block IPs that have exceeded the auth failure threshold.
  const reqIp: string = req.ip ?? "unknown";
  const nowTs = Date.now();
  if (checkAuthBlock(authFailures, reqIp, nowTs)) {
    const rec = authFailures.get(reqIp);
    const retryAfterSeconds = rec ? Math.max(0, Math.ceil((rec.blockedUntil - nowTs) / 1000)) : 0;
    send429(res, buildAuthLockoutError({ retryAfterSeconds, limit: AUTH_FAILURE_MAX, current: rec?.count ?? AUTH_FAILURE_MAX, id: req.body?.id ?? null }));
    return;
  }

  // TEN-2606 — absent credential (OAuth bootstrap probe) is never recorded.
  const auth = resolveBearerAuth(req.headers?.authorization, accessTokens, nowTs, ACCESS_TOKEN_TTL_MS);
  if (auth.status !== "ok") {
    logRequest("DELETE", "auth_fail");
    if (auth.status === "rejected") recordAuthFailure(authFailures, reqIp, nowTs);
    send401(req, res);
    return;
  }
  const apiKey = auth.apiKey;

  const sessionId = req.headers["mcp-session-id"] as string | undefined;
  if (!sessionId) {
    res.status(400).send("Missing Mcp-Session-Id header");
    return;
  }
  if (!sessions.has(sessionId)) {
    // Stale session — per MCP spec § Session Management clauses 3-4, return 404
    // so the client re-initialises without re-authing. Survives Railway restarts.
    process.stderr.write(
      `[HTTP] ${new Date().toISOString()} session_stale DELETE sessionId=${sessionId}\n`,
    );
    res.status(404).send("Session not found — re-initialise");
    return;
  }

  try {
    await runWithAuth({ apiKey, mcpSessionId: sessionId }, async () => {
      const entry = sessions.get(sessionId)!;
      // Fix 3 — Verify the session belongs to the presenting key.
      if (entry.keyHash !== hashKey(apiKey)) {
        res.status(403).json({
          jsonrpc: "2.0",
          error: { code: -32000, message: "Session key mismatch" },
          id: null,
        });
        return;
      }
      await entry.transport.handleRequest(req, res);
      logRequest("DELETE", "ok", sessionId);
    });
  } catch {
    logRequest("DELETE", "error", sessionId);
  }
});

// ── Start ───────────────────────────────────────────────────────────────

process.on("unhandledRejection", (reason) => {
  const msg = reason instanceof Error ? reason.message : String(reason);
  console.error(`[MCP HTTP] Unhandled rejection: ${msg}`);
});

process.on("uncaughtException", (err) => {
  console.error(`[MCP HTTP] Uncaught exception: ${err.stack ?? err.message}`);
  gracefulShutdown();
});

let shuttingDown = false;
async function gracefulShutdown() {
  if (shuttingDown) return;
  shuttingDown = true;
  setTimeout(() => process.exit(1), 3_000).unref();
  console.log("Shutting down...");
  for (const [, entry] of sessions) {
    await entry.transport.close().catch(() => {});
  }
  try {
    await shutdownAnalytics();
  } catch {
    /* best-effort */
  }
  process.exit(0);
}

// Bind all interfaces — Railway/Cloudflare reach the container on its non-loopback IP.
// Loopback-only (127.0.0.1) causes edge 502: the proxy never connects to localhost inside the pod.
const LISTEN_HOST = "0.0.0.0";
const httpServer = app.listen(PORT, LISTEN_HOST, () => {
  console.log(
    `Product Brain MCP HTTP server v${SERVER_VERSION} listening on ${LISTEN_HOST}:${PORT}`,
  );
});
httpServer.on("error", (err: Error) => {
  console.error(`[MCP HTTP] Server error: ${err.message}`);
  process.exit(1);
});

process.on("SIGINT", gracefulShutdown);
process.on("SIGTERM", gracefulShutdown);
